import { DMSProvider, Services } from '@ekhodealer/ekho-common';

import type {
	BankAccount,
	ServicesProvider,
	ServicesProviderSaleData,
} from '@ekhodealer/ekho-common';

import { OnboardingPage } from '../components/onboarding-pages/content';

export const ONBOARDING_GROUP = {
	INVENTORY_CONNECTION: 'inventoryConnection',
	BUSINESS_IDENTITY: 'businessIdentity',
	BANK_ACCOUNT_DETAILS: 'bankAccountDetails',
	PRIMARY_CONTACT: 'primaryContact',
	BANK_ACCOUNT: 'bankAccount',
} as const;

export type OnboardingGroup = (typeof ONBOARDING_GROUP)[keyof typeof ONBOARDING_GROUP];

interface OnboardingValidationResult {
	/** Whether all checks pass. */
	isComplete: boolean;
	/** A dynamic message listing which groups are missing used in the alert. */
	message: string | null;
	/** Which validation groups failed. */
	incompleteGroups: OnboardingGroup[];
}

const GROUP_TO_PAGE: Record<OnboardingGroup, OnboardingPage> = {
	[ONBOARDING_GROUP.INVENTORY_CONNECTION]: OnboardingPage.InventoryConnection,
	[ONBOARDING_GROUP.BUSINESS_IDENTITY]: OnboardingPage.BusinessDetails,
	[ONBOARDING_GROUP.PRIMARY_CONTACT]: OnboardingPage.BusinessDetails,
	[ONBOARDING_GROUP.BANK_ACCOUNT_DETAILS]: OnboardingPage.BankAccount,
	[ONBOARDING_GROUP.BANK_ACCOUNT]: OnboardingPage.BankAccount,
};

/**
 * Returns the earliest onboarding page that has incomplete groups,
 * or null if all groups are complete.
 */
export function getEarliestIncompletePage(groups: OnboardingGroup[]): OnboardingPage | null {
	if (groups.length === 0) return null;
	return Math.min(...groups.map((g) => GROUP_TO_PAGE[g])) as OnboardingPage;
}

/**
 * Validates whether a services provider has completed the minimum onboarding requirements:
 * 1. Inventory connection: CMF is set (Lightspeed) OR dmsProvider is "other"
 * 2. Business identity: name, email, phone, physicalAddress
 * 3. Bank account details: legalName, taxNumber, legalAddress
 * 4. Primary contact: phone, email, fullName
 * 5. Bank account: at least one bank account is connected
 *
 * Returns whether all checks pass, a dynamic message listing which groups are missing,
 * and an array of incomplete group identifiers.
 */
export function hasCompletedMinimumOnboarding(
	servicesProvider: ServicesProvider,
	bankAccounts: BankAccount[],
): OnboardingValidationResult {
	const missingGroups: string[] = [];
	const incompleteGroups: OnboardingGroup[] = [];

	if (!hasInventoryConnection(servicesProvider)) {
		missingGroups.push('inventory connection');
		incompleteGroups.push(ONBOARDING_GROUP.INVENTORY_CONNECTION);
	}
	if (!hasBusinessIdentity(servicesProvider)) {
		missingGroups.push('business details');
		incompleteGroups.push(ONBOARDING_GROUP.BUSINESS_IDENTITY);
	}
	if (!hasBankAccountDetails(servicesProvider)) {
		missingGroups.push('bank account details');
		incompleteGroups.push(ONBOARDING_GROUP.BANK_ACCOUNT_DETAILS);
	}
	if (!hasPrimaryContact(servicesProvider)) {
		missingGroups.push('primary contact');
		incompleteGroups.push(ONBOARDING_GROUP.PRIMARY_CONTACT);
	}
	if (!hasBankAccount(bankAccounts)) {
		missingGroups.push('bank account');
		incompleteGroups.push(ONBOARDING_GROUP.BANK_ACCOUNT);
	}

	if (missingGroups.length === 0) {
		return { isComplete: true, message: null, incompleteGroups: [] };
	}

	const formatted = formatList(missingGroups);
	return {
		isComplete: false,
		message: `Please add the missing ${formatted} before continuing.`,
		incompleteGroups,
	};
}

function hasInventoryConnection(servicesProvider: ServicesProvider): boolean {
	const dmsConfig = servicesProvider.config?.[Services.INVENTORY]?.dmsConnectionConfig;
	if (!dmsConfig) return false;

	if (dmsConfig.dmsProvider === DMSProvider.OTHER) return true;
	if (dmsConfig.dmsProvider === DMSProvider.LIGHTSPEED) {
		const details = dmsConfig.connectionDetails as { cmf?: string } | undefined;
		return !!details?.cmf;
	}

	return false;
}

function hasBusinessIdentity(servicesProvider: ServicesProvider): boolean {
	const data = (servicesProvider.toObject?.() ??
		servicesProvider) as unknown as ServicesProviderSaleData;
	return !!(
		servicesProvider.name &&
		servicesProvider.email &&
		servicesProvider.phone &&
		data.physicalAddress
	);
}

function hasBankAccountDetails(servicesProvider: ServicesProvider): boolean {
	const data = (servicesProvider.toObject?.() ??
		servicesProvider) as unknown as ServicesProviderSaleData;
	return !!(data.legalName && data.taxNumber && data.legalAddress);
}

function hasPrimaryContact(servicesProvider: ServicesProvider): boolean {
	return !!(
		servicesProvider.primaryContact?.phone &&
		servicesProvider.primaryContact?.email &&
		servicesProvider.primaryContact?.fullName
	);
}

function hasBankAccount(bankAccounts: BankAccount[]): boolean {
	return bankAccounts.length > 0;
}

/** Joins items with commas and "and" before the last item. */
function formatList(items: string[]): string {
	if (items.length === 1) return items[0];
	if (items.length === 2) return `${items[0]} and ${items[1]}`;
	return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
